import {
  ClaimedDatabaseResource,
  CloudAccountApiListAbacAccountsRequest,
  DataAccessGroupInfo,
  DataGroupAccessAssignmentResponse,
  DataGroupApiAddItemsRequest,
  DataGroupApiCreateDataGroupRequest,
  DataGroupApiDeleteDataGroupRequest,
  DataGroupApiGetDataGroupRequest,
  DataGroupApiGetDataGroupsSensitivityRequest,
  DataGroupApiGetClaimedResourcesRequest,
  DataGroupApiGetDataAccessGroupsRequest,
  DataGroupApiGetJitAccessGroupsRequest,
  DataGroupApiListAccessAssignmentsRequest,
  DataGroupApiListDataGroupsRequest,
  DataGroupApiListPolicyRolesRequest,
  DataGroupApiReplaceOnDemandAccessRequest,
  DataGroupApiReplacePermanentGroupAccessRequest,
  DataGroupApiReplacePermanentUserAccessRequest,
  DataGroupApiUpdateAccessAssignmentsRequest,
  DataGroupApiUpdateDataGroupRequest,
  DataGroupPolicyRoleResponse,
  DataGroupResponse,
  DataGroupSensitivityResponse,
  JITAccessGroupInfo,
  PageDataGroupListResponse,
  PageCloudAccountResponse,
  IdentityInfo,
  DataGroupApiGetIdentitiesUsingDataRoleRequest,
} from '@ariksa/abac/api';
import {
  DataAccessApiGetAuthorizedIdentitiesRequest,
  DataAccessApiGetDataAccessRolesRequest,
  DataAccessIdentityResponse,
  InsightV2ApiInsightV2AllRequest,
  InsightV2Pagination,
} from '@ariksa/inventory-core/api';
import { JitDataGroupRequestCountsResponse } from '@ariksa/jit/api';
import { call } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeEveryAction } from 'services/utils/takeEveryAction';
import { takeLatestAction } from 'services/utils/takeLatestAction';

import { Abac, InventoryService, JitService } from 'api/index';

import { actions } from './slice';

export function* dataGroupsSaga() {
  yield takeLatestAction(actions.getCloudAccounts, doGetCloudAccounts);
  yield takeLatestAction(actions.getOktaUsers, doGetOktaUsers);
  yield takeLatestAction(
    actions.getIdentitiesUsingDataRole,
    doGetIdentitiesUsingDataRole,
  );
  yield takeLatestAction(actions.getDataGroups, doGetDataGroups);
  yield takeLatestAction(
    actions.getDataGroupsSensitivity,
    doGetDataGroupsSensitivity,
  );
  yield takeLatestAction(
    actions.getDataGroupRequestCounts,
    doGetDataGroupRequestCounts,
  );
  yield takeLatestAction(actions.getClaimedResources, doGetClaimedResources);
  yield takeLatestAction(actions.createDataGroup, doCreateDataGroup);
  yield takeLatestAction(actions.deleteDataGroup, doDeleteDataGroup);
  yield takeLatestAction(actions.updateDataGroup, doUpdateDataGroup);
  yield takeLatestAction(actions.addDataGroupItems, doAddDataGroupItems);
  yield takeLatestAction(actions.getDataGroup, doGetDataGroup);
  yield takeEveryAction(actions.getDataGroupRoles, doGetDataGroupRoles);
  yield takeLatestAction(actions.getRoles, doGetRoles);
  yield takeLatestAction(actions.getUsers, doGetAuthorizedIdentities);
  yield takeLatestAction(
    actions.getAuthorizedIdentities,
    doGetAuthorizedIdentities,
  );
  yield takeLatestAction(actions.getOktaGroups, doGetOktaGroups);
  yield takeLatestAction(
    actions.getCloudRolesWithMasksForADataGroup,
    doGetCloudRolesWithMasksForADataGroup,
  );
  yield takeLatestAction(actions.saveConfiguration, doSaveConfiguration);
  yield takeLatestAction(actions.getConfiguration, doGetConfiguration);
  yield takeLatestAction(
    actions.replaceOnDemandAccess,
    doReplaceOnDemandAccess,
  );
  yield takeLatestAction(
    actions.replacePermanentUserAccess,
    doReplacePermanentUserAccess,
  );
  yield takeLatestAction(
    actions.replacePermanentGroupAccess,
    doReplacePermanentGroupAccess,
  );
  yield takeLatestAction(
    actions.getJitEnabledOktaGroups,
    doGetJitEnabledOktaGroups,
  );
  yield takeLatestAction(actions.getAuthorizedGroups, doGetAuthorizedGroups);
  yield takeLatestAction(actions.getDataAccessRoles, doGetDataAccessRoles);
}

export function* doGetCloudAccounts(
  ctx: QueryContext<
    PageCloudAccountResponse,
    CloudAccountApiListAbacAccountsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.CloudAccounts.listAbacAccounts(ctx.params));
}

export function* doGetOktaUsers(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2AllRequest>,
) {
  yield call(ctx.fetch, () =>
    InventoryService.InsightV2.insightV2All(ctx.params),
  );
}

export function* doGetIdentitiesUsingDataRole(
  ctx: QueryContext<
    IdentityInfo[],
    DataGroupApiGetIdentitiesUsingDataRoleRequest
  >,
) {
  yield call(ctx.fetch, () =>
    Abac.DataGroup.getIdentitiesUsingDataRole(ctx.params),
  );
}

export function* doGetDataGroups(
  ctx: QueryContext<
    PageDataGroupListResponse,
    DataGroupApiListDataGroupsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.listDataGroups(ctx.params));
}

// Fetches sensitivity for a page of data groups in a single bulk request.
export function* doGetDataGroupsSensitivity(
  ctx: QueryContext<
    DataGroupSensitivityResponse[],
    DataGroupApiGetDataGroupsSensitivityRequest
  >,
) {
  yield call(ctx.fetch, () =>
    Abac.DataGroup.getDataGroupsSensitivity(ctx.params),
  );
}

export function* doGetDataGroupRequestCounts(
  ctx: QueryContext<JitDataGroupRequestCountsResponse>,
) {
  yield call(ctx.fetch, () => JitService.Jit.getDataGroupRequestCounts());
}

export function* doGetClaimedResources(
  ctx: QueryContext<
    ClaimedDatabaseResource[],
    DataGroupApiGetClaimedResourcesRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getClaimedResources(ctx.params));
}

export function* doCreateDataGroup(
  ctx: QueryContext<DataGroupResponse, DataGroupApiCreateDataGroupRequest>,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.createDataGroup(ctx.params));
}

export function* doDeleteDataGroup(
  ctx: QueryContext<DataGroupResponse, DataGroupApiDeleteDataGroupRequest>,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.deleteDataGroup(ctx.params));
}

export function* doUpdateDataGroup(
  ctx: QueryContext<DataGroupResponse, DataGroupApiUpdateDataGroupRequest>,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.updateDataGroup(ctx.params));
}

export function* doAddDataGroupItems(
  ctx: QueryContext<any[], DataGroupApiAddItemsRequest>,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.addItems(ctx.params));
}

export function* doGetDataGroup(
  ctx: QueryContext<DataGroupResponse, DataGroupApiGetDataGroupRequest>,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getDataGroup(ctx.params));
}

export function* doGetCloudRolesWithMasksForADataGroup(
  ctx: QueryContext<
    DataGroupPolicyRoleResponse[],
    DataGroupApiListPolicyRolesRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.listPolicyRoles(ctx.params));
}

export function* doGetDataGroupRoles(
  ctx: QueryContext<
    DataAccessGroupInfo[],
    DataGroupApiGetDataAccessGroupsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getDataAccessGroups(ctx.params));
}

export function* doGetAuthorizedIdentities(
  ctx: QueryContext<
    DataAccessIdentityResponse[],
    DataAccessApiGetAuthorizedIdentitiesRequest
  >,
) {
  yield call(ctx.fetch, () =>
    InventoryService.DataAccess.getAuthorizedIdentities(ctx.params),
  );
}

export function* doGetOktaGroups(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2AllRequest>,
) {
  yield call(ctx.fetch, () =>
    InventoryService.InsightV2.insightV2All(ctx.params),
  );
}

export function* doGetRoles(
  ctx: QueryContext<
    DataAccessIdentityResponse[],
    DataAccessApiGetDataAccessRolesRequest
  >,
) {
  yield call(ctx.fetch, () =>
    InventoryService.DataAccess.getDataAccessRoles(ctx.params),
  );
}

export function* doSaveConfiguration(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiUpdateAccessAssignmentsRequest
  >,
) {
  yield call(ctx.fetch, () =>
    Abac.DataGroup.updateAccessAssignments(ctx.params),
  );
}

export function* doGetConfiguration(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiListAccessAssignmentsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.listAccessAssignments(ctx.params));
}

export function* doReplaceOnDemandAccess(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiReplaceOnDemandAccessRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.replaceOnDemandAccess(ctx.params));
}

export function* doReplacePermanentUserAccess(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiReplacePermanentUserAccessRequest
  >,
) {
  yield call(ctx.fetch, () =>
    Abac.DataGroup.replacePermanentUserAccess(ctx.params),
  );
}

export function* doReplacePermanentGroupAccess(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiReplacePermanentGroupAccessRequest
  >,
) {
  yield call(ctx.fetch, () =>
    Abac.DataGroup.replacePermanentGroupAccess(ctx.params),
  );
}

export function* doGetJitEnabledOktaGroups(
  ctx: QueryContext<
    JITAccessGroupInfo[],
    DataGroupApiGetJitAccessGroupsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getJitAccessGroups(ctx.params));
}

export function* doGetAuthorizedGroups(
  ctx: QueryContext<
    JITAccessGroupInfo[],
    DataGroupApiGetJitAccessGroupsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getJitAccessGroups(ctx.params));
}
export function* doGetDataAccessRoles(
  ctx: QueryContext<
    DataGroupAccessAssignmentResponse[],
    DataGroupApiListAccessAssignmentsRequest
  >,
) {
  yield call(ctx.fetch, () => Abac.DataGroup.getDataAccessGroups(ctx.params));
}
